import getCssVar from 'src/utils/getCssVar'
import cssColorToHex from 'src/utils/cssColorToHex'

// Stripe Elements appearance for v7, mapped from the Tailwind 4 tokens
// defined in donate_v7.css (--color-brand-*, --color-danger-*,
// --input-*, --color-gray-*, --font-sans, etc.). Stripe iframes are
// cross-origin and don't inherit CSS variables from the parent, so we
// read the computed values here and pass concrete strings via
// appearance.variables and appearance.rules.
//
// Stripe accepts HEX / rgb() / hsl() but Tailwind ships its palette as
// oklch(), so colors go through cssColorToHex — see the comment there for
// why that needs a canvas. Hex specifically, rather than rgb(), because
// blackbaudAppearance shares the helper and Blackbaud accepts nothing else.
//
// The goal is for a Stripe-rendered PaymentElement input to read the
// same as a v7 InputField sitting next to it: same border width / color,
// same focus-ring color and thickness, same label typography, same
// invalid-state treatment.
//
// Cached on first call: the tokens don't change at runtime within a
// session, and getComputedStyle does enough work that re-reading on
// every Elements remount adds up. If we ever wire partner-tunable
// theming that mutates root tokens at runtime, this cache needs to
// invalidate (or be removed).
let cache

const stripeAppearance = () => {
  if (cache) return cache

  const brand500       = cssColorToHex(getCssVar('--color-brand-500'))
  const brand200       = cssColorToHex(getCssVar('--color-brand-200'))
  const danger500      = cssColorToHex(getCssVar('--color-danger-500'))
  const danger200      = cssColorToHex(getCssVar('--color-danger-200'))
  const inputBg        = cssColorToHex(getCssVar('--input-bg'))
  const inputText      = cssColorToHex(getCssVar('--input-text'))
  const inputBorder    = cssColorToHex(getCssVar('--input-border'))
  const labelColor     = cssColorToHex(getCssVar('--color-gray-700'))
  const placeholder    = cssColorToHex(getCssVar('--color-gray-500'))
  const inputBorderW   = getCssVar('--input-border-width')
  const inputPaddingY  = getCssVar('--input-padding-y')
  const inputPaddingX  = getCssVar('--input-padding-x')
  const inputRadius    = getCssVar('--input-radius')
  const fontSans       = getCssVar('--default-font-family')

  return cache = {
    variables: {
      colorPrimary:         brand500,
      colorText:            inputText,
      colorTextPlaceholder: placeholder,
      colorDanger:          danger500,
      colorBackground:      inputBg,
      borderRadius:         inputRadius,
      fontFamily:           fontSans,
      // Tailwind doesn't apply -webkit-font-smoothing by default;
      // 'never' tells Stripe to skip its own antialiasing tweaks so
      // the rendered Stripe text weight matches our InputField text
      // sitting next to it.
      fontSmooth:           'never',
      fontLineHeight:       '1.5',
      // Match Tailwind's text-base for input text and text-sm for
      // labels — Stripe re-derives label / error sizes from these.
      fontSizeBase:         '1rem',
      fontSizeSm:           '0.875rem',
      fontSizeXs:           '0.75rem',
      fontWeightNormal:     '400',
      fontWeightMedium:     '500',
      // Gap between adjacent fields in a multi-field block (e.g. card
      // number + expiry + CVC). Mirrors gap-3 / gap-2 we use on v7's
      // own form rows.
      spacingGridColumn:    '0.5rem',
      spacingGridRow:       '0.75rem',
    },
    rules: {
      // Label: matches InputField's `block text-sm font-medium
      // text-gray-700 mb-1` baseline.
      '.Label': {
        fontSize:     '0.875rem',
        fontWeight:   '500',
        color:        labelColor,
        marginBottom: '0.25rem',
      },

      // Error message under a field: matches InputField's `mt-1
      // text-sm text-danger`. Color comes from colorDanger above.
      '.Error': {
        fontSize:  '0.875rem',
        marginTop: '0.25rem',
      },

      // Input chrome — explicit border width / padding / radius so
      // the Stripe iframe lands the same-sized input as our
      // .form-control, not the slightly thinner Stripe default. The
      // transition lets the focus ring fade in/out the way our own
      // .input-group rules do.
      //
      // lineHeight is pinned explicitly because Stripe's
      // `fontLineHeight` variable doesn't actually apply to .Input —
      // without an override here, each browser falls back to its
      // own <input> default (~1.15 in Chrome, ~1.375 Safari, ~1.44
      // Firefox), which makes the rendered height drift 1–6px from
      // our .form-control on the page.
      '.Input': {
        borderWidth:   inputBorderW,
        borderColor:   inputBorder,
        borderRadius:  inputRadius,
        paddingTop:    inputPaddingY,
        paddingBottom: inputPaddingY,
        paddingLeft:   inputPaddingX,
        paddingRight:  inputPaddingX,
        lineHeight:    '1.5',
        boxShadow:     'none',
        transition:    'border-color 150ms, box-shadow 150ms',
      },

      // Focused: switch to brand color and add the brand-200 ring at
      // the same width as the border (matches v7's
      // .input-group:has(.input-group-control:focus) rule).
      '.Input:focus': {
        borderColor: brand500,
        boxShadow:   `0 0 0 ${inputBorderW} ${brand200}`,
      },

      // Invalid: solid danger border, no ring at rest.
      '.Input--invalid': {
        borderColor: danger500,
        boxShadow:   'none',
      },

      // Invalid + focused: keep the danger border, add a danger ring
      // (parallels the brand pattern above).
      '.Input--invalid:focus': {
        borderColor: danger500,
        boxShadow:   `0 0 0 ${inputBorderW} ${danger200}`,
      },

      // Tabs are the PaymentElement's payment-method picker when
      // multiple methods are configured. v7 currently constrains to
      // one method per Elements scope so this rarely renders, but
      // styling it now keeps Stripe's UI consistent with the rest of
      // v7 if a future method ever surfaces an additional tab.
      '.Tab': {
        borderWidth:     inputBorderW,
        borderColor:     inputBorder,
        borderRadius:    inputRadius,
        backgroundColor: inputBg,
      },
      '.Tab:hover': {
        borderColor: brand500,
      },
      '.Tab--selected': {
        borderColor: brand500,
        boxShadow:   `0 0 0 ${inputBorderW} ${brand200}`,
      },

      // Block is PaymentElement's grouped-fields container (e.g. the
      // bank account block, the card-with-name-and-zip block).
      // Default Stripe gives it its own subtle border; match the
      // input chrome so it doesn't read like a separate visual layer.
      '.Block': {
        borderWidth:  inputBorderW,
        borderColor:  inputBorder,
        borderRadius: inputRadius,
      },
    },
  }
}

export default stripeAppearance
